/**
 * Staff Dashboard widget: at-a-glance counts of pending Patient Portal work.
 *
 * Fed by `GET /api/staff/portal/inbox-summary`. Renders quietly when
 * counts are zero (no clutter) but pops with a subtle accent when any
 * queue has waiting items so buyers/pharmacists notice.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Upload, RefreshCw, MessageCircle, Inbox, ArrowRight, AlertTriangle } from 'lucide-react';
import api from '../lib/api';

const ITEMS = [
  { key: 'prescriptions_pending', label: 'Prescription uploads', icon: Upload,        to: '/customers/prescriptions', accent: 'text-emerald-700 bg-emerald-100' },
  { key: 'refills_pending',       label: 'Refill requests',      icon: RefreshCw,     to: '/customers/refills',       accent: 'text-sky-700 bg-sky-100' },
  { key: 'unread_messages',       label: 'Unread messages',      icon: MessageCircle, to: '/customers/conversations', accent: 'text-violet-700 bg-violet-100' },
  { key: 'adrs_pending',          label: 'Reaction reports',     icon: AlertTriangle, to: '/customers/reactions',     accent: 'text-red-700 bg-red-100' },
];

export default function PortalInboxWidget() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/staff/portal/inbox-summary')
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ prescriptions_pending: 0, refills_pending: 0, unread_messages: 0, adrs_pending: 0 }); });
    return () => { alive = false; };
  }, []);

  if (!data) return null;
  const total = (data.prescriptions_pending || 0) + (data.refills_pending || 0) + (data.unread_messages || 0) + (data.adrs_pending || 0);
  const hot = total > 0;

  return (
    <Card
      data-testid="portal-inbox-widget"
      className={`transition-all ${hot ? 'border-emerald-200 shadow-[0_4px_24px_-8px_rgba(5,150,105,0.25)]' : 'border-slate-100'}`}
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className={`w-9 h-9 rounded-xl grid place-items-center ${hot ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
              <Inbox className="w-4 h-4" />
            </div>
            <div>
              <div className="font-semibold text-slate-900">Patient Portal Inbox</div>
              <div className="text-xs text-slate-500">
                {hot ? `${total} item${total === 1 ? '' : 's'} awaiting your team` : 'No pending items · you\u2019re all caught up'}
              </div>
            </div>
          </div>
          {hot && (
            <Badge className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="portal-inbox-total">{total}</Badge>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {ITEMS.map((it) => {
            const v = data[it.key] || 0;
            return (
              <Link
                key={it.key}
                to={it.to}
                data-testid={`portal-inbox-${it.key}`}
                className={`flex items-center justify-between px-3 py-2.5 rounded-xl border ${
                  v > 0 ? 'border-emerald-200 hover:bg-emerald-50' : 'border-slate-100 hover:bg-slate-50'
                } transition-all group`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`w-8 h-8 rounded-lg grid place-items-center ${it.accent}`}>
                    <it.icon className="w-4 h-4" />
                  </div>
                  <div className="text-sm text-slate-700 truncate">{it.label}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <span className={`text-xl font-bold tabular-nums ${v > 0 ? 'text-slate-900' : 'text-slate-300'}`}>{v}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all" />
                </div>
              </Link>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
